import type { RequestConfig } from '@/infrastructure/interfaces/HttpClient'
import type { RequestInterceptor, ResponseProcessor } from './HttpClient.types'

/**
 * 基础 Header 拦截器：注入 app-id, language 等静态配置
 */
export class HeaderInterceptor implements RequestInterceptor {
  constructor(private readonly headers: Record<string, string>) {}

  intercept(config: RequestConfig): RequestConfig {
    const headers: Record<string, string> = {}

    if (this.headers) {
      for (const [key, value] of Object.entries(this.headers)) {
        headers[key.toLowerCase()] = value
      }
    }

    if (config.headers) {
      for (const [key, value] of Object.entries(config.headers)) {
        headers[key.toLowerCase()] = value
      }
    }

    if (
      config.body &&
      !(config.body instanceof FormData) &&
      !headers['content-type']
    ) {
      headers['content-type'] = 'application/json'
    }

    return { ...config, headers }
  }
}

/**
 * 授权拦截器：从原生 Storage 注入 Token
 */
export class AuthInterceptor implements RequestInterceptor {
  constructor(
    private readonly storage: Storage,
    private readonly tokenKey: string,
  ) {}

  intercept(config: RequestConfig): RequestConfig {
    const token = this.storage.getItem(this.tokenKey)
    if (token) {
      const headers = { ...config.headers }
      const hasAuth = Object.keys(headers).some(
        (k) => k.toLowerCase() === 'authorization',
      )
      if (!hasAuth) {
        headers.Authorization = `Bearer ${token}`
      }
      return { ...config, headers }
    }
    return config
  }
}

/**
 * 业务协议响应处理器
 */
export class BusinessResponseProcessor implements ResponseProcessor {
  constructor(private readonly onUnauthorized?: () => void) {}

  async process<T>(response: Response): Promise<T> {
    // 1. 处理系统级错误
    if (response.status === 401) {
      this.onUnauthorized?.()
      throw new Error('Session expired, please sign in again')
    }

    if (!response.ok) {
      const text = await response.text()
      throw new Error(`HTTP Error: ${response.status} - ${text}`)
    }

    // 2. 解析 JSON
    const data = await response.json()
    const { ok, message, result, error_message } = data

    // 3. 处理业务级错误 (ok: false)
    if (ok === false) {
      throw new Error(error_message || message || 'Unknown Business Error')
    }

    return result as T
  }
}
